import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "../lib/api";
import { useSettings } from "../lib/settings";
import { Skull, Check, Loader2 } from "lucide-react";

function fullDate(d) {
  const dt = new Date(d);
  const weekday = dt.toLocaleDateString("en-GB", { weekday: "long" });
  const month = dt.toLocaleDateString("en-GB", { month: "long" });
  return `${weekday} ${dt.getDate()} ${month} ${dt.getFullYear()}`;
}

export default function AuditionSelect() {
  const { token } = useParams();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmedDate, setConfirmedDate] = useState(null);
  const [cantStep, setCantStep] = useState(false);
  const [cantDone, setCantDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/audition-select/${token}`)
      .then(({ data }) => {
        setData(data);
        if (data.audition_dates?.length === 1) setSelected(data.audition_dates[0].id);
      })
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true); setError(null);
    try {
      const { data: res } = await axios.post(`${API}/public/audition-select/${token}`, { audition_date_ids: [selected] });
      setConfirmedDate(res.audition_date);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const cantAttend = async () => {
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API}/public/audition-select/${token}/cant-attend`);
      setCantDone(true);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#050505] text-[#F2EBE1] flex items-center justify-center py-12 px-6">
      <div className="max-w-2xl w-full">{children}</div>
    </div>
  );

  const Brand = () => (
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-3">
        <Skull size={28} className="text-blood" strokeWidth={1.5} />
        <span className="font-display text-2xl font-bold tracking-[0.04em] text-blood">SCARE AUDITIONS<span className="text-[0.45em] font-semibold tracking-normal align-top opacity-80 ml-1">.co.uk</span></span>
      </div>
    </div>
  );

  // Invalid / expired link
  if (error && !data) {
    const orgEmail = settings.org_email;
    return (
      <Shell>
        <Brand />
        <div className="text-center border border-[#262626] bg-[#0A0A0A] p-10">
          <p className="font-display text-3xl uppercase mb-3">Link Issue</p>
          <p className="text-[#A19D94]" data-testid="audition-error">This audition link is invalid or could not be found.</p>
          <p className="text-[#A19D94] mt-2">
            Please get in touch{orgEmail ? <> at <a href={`mailto:${orgEmail}`} className="text-blood hover:underline" data-testid="audition-error-email">{orgEmail}</a></> : <> with {settings.org_name || "the organisation"}</>} and we'll sort it out.
          </p>
        </div>
      </Shell>
    );
  }

  if (!data) return <Shell><div className="flex justify-center"><Loader2 className="animate-spin text-blood" /></div></Shell>;

  const firstName = data.applicant.first_name || data.applicant.full_name?.split(" ")[0];

  // Just confirmed now
  if (confirmedDate) {
    return (
      <Shell>
        <Brand />
        <div className="text-center animate-fade-in border border-[#262626] bg-[#0A0A0A] p-10" data-testid="audition-confirmed">
          <div className="inline-flex items-center justify-center w-20 h-20 border border-blood mb-6"><Check size={36} className="text-blood" strokeWidth={1.5} /></div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-3">You're Confirmed</h1>
          <p className="text-[#A19D94] text-lg">You're confirmed for <span className="text-[#F2EBE1] font-semibold">{confirmedDate.name}</span> on {fullDate(confirmedDate.date)}. We look forward to seeing you!</p>
        </div>
      </Shell>
    );
  }

  // Already confirmed previously (token used)
  if (data.used) {
    const sel = data.selected_audition_date;
    if (data.outcome === "cant_attend") {
      return (
        <Shell>
          <Brand />
          <div className="text-center border border-[#262626] bg-[#0A0A0A] p-10" data-testid="audition-cant-attend-recorded">
            <h1 className="font-display text-3xl font-black uppercase tracking-tighter mb-3">Response Recorded</h1>
            <p className="text-[#A19D94] text-lg">We're sorry you can't make it. We'll be in touch to discuss alternative options.</p>
          </div>
        </Shell>
      );
    }
    return (
      <Shell>
        <Brand />
        <div className="text-center border border-[#262626] bg-[#0A0A0A] p-10" data-testid="audition-already-confirmed">
          <div className="inline-flex items-center justify-center w-20 h-20 border border-blood mb-6"><Check size={36} className="text-blood" strokeWidth={1.5} /></div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tighter mb-3">Already Confirmed</h1>
          <p className="text-[#A19D94] text-lg">
            {sel
              ? <>You've already confirmed your attendance for <span className="text-[#F2EBE1] font-semibold">{sel.name}</span> on {fullDate(sel.date)}. We look forward to seeing you!</>
              : <>You've already confirmed your audition attendance. We look forward to seeing you!</>}
          </p>
        </div>
      </Shell>
    );
  }

  const dates = data.audition_dates || [];
  const single = dates.length === 1;

  // Submitted "can't attend"
  if (cantDone) {
    return (
      <Shell>
        <Brand />
        <div className="text-center border border-[#262626] bg-[#0A0A0A] p-10" data-testid="audition-cant-attend-done">
          <h1 className="font-display text-3xl font-black uppercase tracking-tighter mb-3">Response Recorded</h1>
          <p className="text-[#A19D94] text-lg">We're sorry you can't make it. We'll be in touch to discuss alternative options.</p>
        </div>
      </Shell>
    );
  }

  // "Can't attend" confirmation step
  if (cantStep) {
    return (
      <Shell>
        <Brand />
        <div className="text-center border border-[#262626] bg-[#0A0A0A] p-10" data-testid="cant-attend-confirm">
          <h1 className="font-display text-3xl font-black uppercase tracking-tighter mb-3">Unable to Attend?</h1>
          <p className="text-[#A19D94] mb-8 text-lg">We're sorry you can't make it. We'll be in touch to discuss alternative options.</p>
          {error && <p className="mb-4 text-sm text-blood" data-testid="cant-attend-error">{error}</p>}
          <div className="flex gap-3 justify-center">
            <button onClick={() => { setCantStep(false); setError(null); }} disabled={submitting}
              className="px-6 py-3 border border-[#262626] text-[#A19D94] uppercase text-sm tracking-wider hover:border-[#3a3835] transition-colors disabled:opacity-40" data-testid="cant-attend-back">Back</button>
            <button onClick={cantAttend} disabled={submitting}
              className="px-8 py-3 bg-blood text-white uppercase text-sm tracking-wider hover:bg-[#FF3333] transition-colors disabled:opacity-40" data-testid="cant-attend-confirm-btn">{submitting ? "Submitting…" : "Confirm"}</button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Brand />
      <div className="text-center mb-8">
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-2">You've Been Invited to Audition</h1>
        <p className="text-[#A19D94]">
          Hi {firstName} — you've been invited to audition for <span className="text-blood">{data.org_name}</span>.
          {single
            ? " Please confirm your attendance for the date below."
            : " We have the following audition dates available — please select the one you are able to attend."}
        </p>
      </div>

      {dates.length === 0 ? (
        <p className="text-center text-[#6B6862]">No audition dates are available on this invite. Please contact us.</p>
      ) : (
        <div className="space-y-3">
          {dates.map((au) => {
            const sel = selected === au.id;
            return (
              <button key={au.id} type="button" onClick={() => setSelected(au.id)}
                data-testid={`audition-${au.id}`}
                className={`w-full text-left p-5 border transition-all ${sel ? "border-blood bg-blood/5" : "border-[#262626] hover:border-[#3a3835]"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-xl uppercase tracking-tight">{au.name}</div>
                    <div className="font-mono text-xs text-[#A19D94] mt-1">{fullDate(au.date)} · {au.start_time}–{au.end_time}</div>
                    {au.location && <div className="text-sm text-[#A19D94] mt-1">{au.location}</div>}
                  </div>
                  {!single && (
                    <span className={`w-5 h-5 shrink-0 rounded-full border flex items-center justify-center ${sel ? "border-blood" : "border-[#3a3835]"}`}>
                      {sel && <span className="w-2.5 h-2.5 rounded-full bg-blood" />}
                    </span>
                  )}
                  {single && sel && <Check className="text-blood shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {error && <p className="mt-4 text-sm text-blood text-center" data-testid="audition-submit-error">{error}</p>}

      {dates.length > 0 && (
        <button
          onClick={submit}
          disabled={submitting || !selected}
          data-testid="audition-confirm-btn"
          className="mt-8 w-full py-4 bg-blood text-white font-display uppercase tracking-wider hover:bg-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Confirming…" : "Confirm My Attendance"}
        </button>
      )}

      {dates.length > 0 && (
        <button
          onClick={() => { setError(null); setCantStep(true); }}
          data-testid="cant-attend-link"
          className="mt-4 w-full text-center text-sm text-[#6B6862] hover:text-[#A19D94] underline underline-offset-4 transition-colors"
        >
          {single ? "I'm unable to attend this date" : "I can't make any of these dates"}
        </button>
      )}
    </Shell>
  );
}
